import { z } from 'zod';
/** ISO-8601 instant, always stored and transmitted in UTC. */
export const isoDateTime = z.string().datetime({ offset: true });
/** Calendar day in the user's local timezone, `YYYY-MM-DD`. */
export const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
/**
 * A local wall-clock datetime with no timezone offset, `YYYY-MM-DDTHH:MM:SS`.
 *
 * Deliberately NOT an instant. A medication slot is "20:00 in whatever the
 * local evening is", which is a wall-clock concept — pinning it to an instant
 * would silently shift the dose by an hour across a DST boundary. Distinct
 * from `isoDateTime`, which is for real moments like `takenAt`.
 */
export const localDateTime = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'expected YYYY-MM-DDTHH:MM:SS');
/**
 * The 0-100 belief/intensity scale used throughout CBT worksheets. Kept as a
 * branded-ish alias so it is obvious at call sites which scale is in play —
 * this app also uses 1-5 Likert scales for check-ins, and mixing them silently
 * would corrupt every trend line.
 */
export const percent = z.number().int().min(0).max(100);
/** 1-5 Likert, used for the fast daily check-in sliders. */
export const likert5 = z.number().int().min(1).max(5);
export const id = z.string().min(1);
export const paginationQuery = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    /**
     * One day, by the frozen `day` column — not an instant range. `from`/`to`
     * filter the instant, so an entry said at 00:53 and filed under the night
     * before fell outside its own day (14 Oct 2026).
     */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export const apiError = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
/**
 * How a record came to exist. Worth tracking: entries dictated in the middle of
 * a rough moment read very differently from ones typed calmly a day later, and
 * the weekly report leans on that distinction.
 */
export const entrySource = z.enum(['manual', 'voice', 'ai_assisted', 'sos', 'imported']);
export const userRole = z.enum(['member', 'clinician', 'partner']);
/** Ascending, so a gate is a comparison rather than a list of cases. */
export const AI_LEVELS = ['off', 'transcribe', 'summarise', 'reflect', 'converse'];
/** Does the chosen level reach what a piece of the app needs? */
export function aiLevelAllows(level, needs) {
    return AI_LEVELS.indexOf(level) >= AI_LEVELS.indexOf(needs);
}
//# sourceMappingURL=common.js.map